import { ComponentChildren } from "preact";
import { useLocation } from "preact-iso";
import { toast } from "react-hot-toast";
import {
  apiUrl,
  freePhoneVerifiedTrialRuns,
  stripeCheckoutPath,
  type Tier,
  tiers,
} from "../../constants.ts";
import { useAuth, useQuery } from "./db.ts";
import { LoginCta } from "./login.tsx";

export const freeTrialTerms =
  `Up to ${freePhoneVerifiedTrialRuns} free LLM runs. No credit card required.`;

export const runExplanation =
  "An LLM run is one AI model call. One message or task can use multiple runs.";

export const recurringPlanTerms =
  "Recurring scheduled actions require a paid plan.";

const Feature = ({ children }: { children: ComponentChildren }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-accent-muted text-accent-light dark:text-accent mt-0.5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-3.5 h-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5 13l4 4L19 7"
        />
      </svg>
    </span>
    <span className="text-text-secondary-light dark:text-text-secondary">
      {children}
    </span>
  </li>
);

const formatLimit = (value: number | null) =>
  value === null ? "Unlimited" : value.toLocaleString("en-US");

const parseCheckoutResponse = async (res: Response) => {
  const text = await res.text();
  if (!res.ok) throw new Error(text || "checkout failed");
  return JSON.parse(text) as { url: string };
};

const tierFeatures = (tier: Tier): string[] => [
  tier.name === "Free"
    ? freeTrialTerms
    : `${formatLimit(tier.agentRunsPerMonth)} LLM runs/month`,
  ...tier.features,
];

const highlightStyles: Record<string, { title: string; price: string }> = {
  Standard: {
    title: "text-accent-light dark:text-accent",
    price: "text-accent-light dark:text-accent",
  },
  Pro: {
    title: "text-[#a78bfa]",
    price: "text-[#a78bfa]",
  },
};

const PricingCard = (
  { tier, onSubscribe, buttonText, isCurrentPlan }: {
    tier: Tier;
    onSubscribe?: (priceId: string) => void;
    buttonText: string;
    isCurrentPlan?: boolean;
  },
) => {
  const styles = highlightStyles[tier.name];
  return (
    <article
      aria-label={`${tier.name} plan`}
      className="flex flex-col h-full bg-surface-raised-light dark:bg-surface-raised rounded-xl border border-border-subtle-light dark:border-border-subtle p-6 transition-all hover:border-accent/40 hover:-translate-y-1 hover:shadow-lg"
    >
      <h3
        className={`text-lg font-display font-bold mb-2 flex items-center justify-between ${
          styles?.title || "text-text-primary-light dark:text-text-primary"
        }`}
      >
        {tier.emoji} {tier.name}
      </h3>
      <div
        className={`text-4xl font-extrabold mb-1 ${
          styles?.price ||
          "text-text-primary-light dark:text-text-primary"
        }`}
      >
        ${tier.price}
        <span className="text-sm font-medium text-text-tertiary-light dark:text-text-tertiary ml-1">
          /month
        </span>
      </div>
      <div className="w-full h-px bg-border-subtle-light dark:bg-border-subtle my-4" />
      <ul className="space-y-3 text-sm flex-grow">
        {tierFeatures(tier).map((f, i) => <Feature key={i}>{f}</Feature>)}
      </ul>
      {tier.name === "Free" && (
        isCurrentPlan
          ? (
            <button
              type="button"
              disabled
              className="mt-6 w-full py-2.5 px-4 rounded-lg font-semibold bg-surface-raised border border-border-subtle text-text-tertiary cursor-default"
            >
              Current plan
            </button>
          )
          : <LoginCta text="Try free" className="mt-6 w-full" />
      )}
      {tier.stripePriceId && onSubscribe && (
        <button
          type="button"
          disabled={isCurrentPlan}
          onClick={() => !isCurrentPlan && onSubscribe(tier.stripePriceId!)}
          className={`mt-6 w-full py-2.5 px-4 rounded-lg font-semibold transition-colors ${
            isCurrentPlan
              ? "bg-surface-raised border border-border-subtle text-text-tertiary cursor-default"
              : "text-white bg-accent hover:bg-accent-hover cursor-pointer"
          }`}
        >
          {buttonText}
        </button>
      )}
    </article>
  );
};

const sharedFeatures = [
  "Unlimited agents",
  "Unlimited training links",
  "Analytics",
  "Embed anywhere",
];

export const PricingSection = () => {
  const authState = useAuth();
  const { route } = useLocation();
  const { data } = useQuery(
    authState.user?.id
      ? {
        $users: {
          $: { where: { id: authState.user.id } },
        },
      }
      : null,
  );
  const currentUser = data?.$users?.[0];
  const email = authState.user?.email || currentUser?.email;
  const hasEmail = Boolean(email);
  const userId = currentUser?.id;
  const currentTier = currentUser?.tier ?? "Free";
  const hasActiveSub = Boolean(currentUser?.stripeSubscriptionId);

  const handleSubscribe = (priceId: string) => {
    if (!hasEmail) return route("/login");
    toast.promise(
      fetch(`${apiUrl}/${stripeCheckoutPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId, email, userId }),
      })
        .then(parseCheckoutResponse)
        .then(({ url }) => {
          globalThis.location.href = url;
        }),
      {
        loading: "Opening checkout...",
        success: "Redirecting...",
        error: (error) => `Checkout failed: ${error.message}`,
      },
    );
  };
  return (
    <div className="w-full max-w-screen-2xl mx-auto px-4">
      <h2 className="text-3xl font-display font-bold mb-3 text-center text-text-primary-light dark:text-text-primary">
        Simple, Transparent Pricing
      </h2>
      <div className="max-w-2xl mx-auto mb-8 text-center text-sm text-text-secondary-light dark:text-text-secondary space-y-2">
        <p>Prices are in USD. Paid plans renew monthly. Cancel in Settings.</p>
        <p>{runExplanation}</p>
        <p>{recurringPlanTerms}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {tiers.filter((t) => !("hideInPricingPage" in t && t.hideInPricingPage))
          .map((tier) => {
            const isCurrentPlan = hasEmail && currentTier === tier.name;
            const buttonText = !hasEmail
              ? `Sign in to choose ${tier.name}`
              : isCurrentPlan
              ? "Current plan"
              : hasActiveSub
              ? `Switch to ${tier.name}`
              : `Subscribe to ${tier.name}`;
            return (
              <PricingCard
                tier={tier}
                key={tier.name}
                onSubscribe={handleSubscribe}
                buttonText={buttonText}
                isCurrentPlan={isCurrentPlan}
              />
            );
          })}
      </div>
      <p className="text-center text-sm text-text-tertiary-light dark:text-text-tertiary mt-8">
        All plans include: {sharedFeatures.join(" · ")}
      </p>
    </div>
  );
};
